import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import "./PublicApp.css";
import { modulesViewerContent } from "./modulesViewerContent";

type CapabilityCode = "PLATFORM_BASE" | "INTELLIGENCE" | "VAULT" | "OPERATE" | "SHOP";
type CapabilitySlug = "platform-base" | "intelligence" | "vault" | "operate" | "shop" | "forge";

type CapabilityItem = {
  code: CapabilityCode;
  slug: CapabilitySlug;
  displayName: string;
  eyebrow: string;
  shortDescription: string;
  outcomePhrase: string;
  route: string;
  themeClass: string;
};

type ExtensionItem = {
  displayName: string;
  eyebrow: string;
  shortDescription: string;
  outcomePhrase: string;
  route: string;
  buildDescription: string;
  buildItems: string[];
  connectDescription: string;
  connectItems: string[];
};

type ConnectorPositions = {
  base: number;
  intelligence: number;
  vault: number;
  operateShop: number;
};

type ViewMode = "overview" | "detail";

const VIEW_TRANSITION_MS = 560;

const capabilitySlugSet: ReadonlySet<CapabilitySlug> = new Set([
  "platform-base",
  "intelligence",
  "vault",
  "operate",
  "shop",
  "forge",
]);

const capabilities: CapabilityItem[] = [
  {
    code: "PLATFORM_BASE",
    slug: "platform-base",
    displayName: "Platform Base",
    eyebrow: "Shared Foundation",
    shortDescription: "Secure identity, governance, workflow, evidence, and integration services across aviOS.",
    outcomePhrase: "Trusted by design.",
    route: "/capabilities/platform-base",
    themeClass: "theme-platform",
  },
  {
    code: "INTELLIGENCE",
    slug: "intelligence",
    displayName: "Vault - Intelligence",
    eyebrow: "Shared Intelligence",
    shortDescription: "Connect aircraft context, technical information, parts, and human expertise through trusted AI.",
    outcomePhrase: "Understand. Investigate. Coordinate.",
    route: "/capabilities/intelligence",
    themeClass: "theme-intelligence",
  },
  {
    code: "VAULT",
    slug: "vault",
    displayName: "Vault - Records",
    eyebrow: "Governed Evidence",
    shortDescription: "Preserve aircraft records and maintenance evidence with provenance, traceability, and controlled access.",
    outcomePhrase: "Preserve. Retrieve. Prove.",
    route: "/capabilities/vault",
    themeClass: "theme-vault",
  },
  {
    code: "OPERATE",
    slug: "operate",
    displayName: "Operate",
    eyebrow: "Aircraft Operations",
    shortDescription: "Manage maintenance obligations, aircraft status, compliance evidence, and operational readiness.",
    outcomePhrase: "Know what must be maintained.",
    route: "/capabilities/operate",
    themeClass: "theme-operate",
  },
  {
    code: "SHOP",
    slug: "shop",
    displayName: "Shop",
    eyebrow: "Maintenance Execution",
    shortDescription: "Plan, execute, inspect, close, and deliver maintenance work.",
    outcomePhrase: "Complete the work with continuity.",
    route: "/capabilities/shop",
    themeClass: "theme-shop",
  },
];

const extensions: ExtensionItem = {
  displayName: "Forge",
  eyebrow: "COMPOSABLE BY DESIGN",
  shortDescription: "Turn expertise into operational intelligence.",
  outcomePhrase: "Transform expertise into capability.",
  route: "/capabilities/forge",
  buildDescription: "Create new capabilities.",
  buildItems: ["BYOM", "Co-Build", "Ironfleet Network"],
  connectDescription: "Connect existing systems.",
  connectItems: ["Integrations", "Workflow Extensions", "Data Migration"],
};

function stackArea(code: CapabilityCode): "base" | "intelligence" | "vault" | "operate" | "shop" {
  switch (code) {
    case "PLATFORM_BASE":
      return "base";
    case "INTELLIGENCE":
      return "intelligence";
    case "VAULT":
      return "vault";
    case "OPERATE":
      return "operate";
    case "SHOP":
      return "shop";
    default:
      return "base";
  }
}

function asConnectorStyles(positions: ConnectorPositions): CSSProperties {
  return {
    "--y-base": `${positions.base}px`,
    "--y-intelligence": `${positions.intelligence}px`,
    "--y-vault": `${positions.vault}px`,
    "--y-operate-shop": `${positions.operateShop}px`,
  } as CSSProperties;
}

function normalizePathToSlug(pathname: string): CapabilitySlug | null {
  const trimmed = pathname.trim().replace(/\/+$/, "");
  if (!trimmed.startsWith("/capabilities/")) {
    return null;
  }
  const slugCandidate = trimmed.slice("/capabilities/".length);
  if (capabilitySlugSet.has(slugCandidate as CapabilitySlug)) {
    return slugCandidate as CapabilitySlug;
  }
  return null;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function capabilityPath(slug: CapabilitySlug): string {
  return `/capabilities/${slug}`;
}

function placeholderDescription(label: string): string {
  return `${label} detail content will be added next using this capability detail shell.`;
}

function capabilityLabel(slug: CapabilitySlug): { label: string; eyebrow: string } {
  const moduleContent = modulesViewerContent.modulesBySlug[slug];
  if (!moduleContent) {
    return { label: "Capability", eyebrow: "CAPABILITY" };
  }
  return { label: moduleContent.title, eyebrow: moduleContent.eyebrow };
}

type StandardModuleSlug = "intelligence" | "vault" | "operate" | "shop";

const moduleAccentClass: Record<string, string> = {
  steel: "theme-platform",
  "aviation-blue": "theme-intelligence",
  "teal-blue": "theme-vault",
  "deep-blue": "theme-operate",
  teal: "theme-shop",
  violet: "theme-forge",
};

const pricingBasisLabelByCode: Record<string, string> = {
  PER_TAIL: "Per Tail",
  PER_USER: "Per User",
  PER_SITE: "Per Site",
  PER_ORGANIZATION: "Per Organization",
  PER_CREDIT: "Per Credit",
  PER_CERTIFICATE: "Per Certificate",
  PER_PACKAGE: "Per Package",
  INCLUDED: "Included",
  CUSTOM: "Custom",
};

const workflowTreatmentByModule: Record<
  CapabilitySlug,
  { separator: "arrow" | "dot"; loopBack: boolean }
> = {
  "platform-base": {
    separator: "dot",
    loopBack: false,
  },
  intelligence: {
    separator: "arrow",
    loopBack: false,
  },
  vault: {
    separator: "arrow",
    loopBack: false,
  },
  operate: {
    separator: "arrow",
    loopBack: true,
  },
  shop: {
    separator: "arrow",
    loopBack: false,
  },
  forge: {
    separator: "arrow",
    loopBack: false,
  },
};

type WireframeFeatureItem = { name: string; description: string; tag?: string; tags?: string[]; details?: string[] };
type WireframeAddonItem = { name: string; tags?: string; description: string };

type WireframeModuleContent = {
  eyebrow: string;
  moduleName: string;
  valueLine: string;
  tagline: string;
  pricingRange: string;
  pricingUnitBadge: string;
  pricingNote?: string;
  pricingPillLabel?: string;
  features: WireframeFeatureItem[];
  addOns: WireframeAddonItem[];
  whoUses: string[];
  contextTitle: string;
  contextName: string;
  contextSubtitle: string;
  contextBullets: string[];
  contextChips: string[];
  governanceNote: string;
  creditNote: string;
  contextLink?: string;
  regulatoryAnchors: string[];
  flow: string[];
};

const standardWireframeContentByModule: Record<StandardModuleSlug, WireframeModuleContent> = {
  vault: {
    eyebrow: "GOVERNED EVIDENCE",
    moduleName: "Vault - Records",
    valueLine: "Preserve aircraft records, OEM documentation, and organizational documents with provenance and traceability.",
    tagline: "Preserve. Retrieve. Prove.",
    pricingRange: "$899 – $3,599 per aircraft / year",
    pricingUnitBadge: "Per tail",
    features: [
      { name: "Records Digitization", tag: "Tail", description: "Convert legacy records into structured, searchable digital evidence." },
      { name: "Back-to-Birth Tail Record", tag: "Tail", description: "Governed, complete life-of-aircraft traceability from origin forward." },
      { name: "OEM Knowledge", tags: ["Model", "Tail"], description: "Access OEM procedures and guidance by model and aircraft context." },
      { name: "Troubleshooting", tag: "Tail", description: "Structured reasoning for faults and symptoms." },
      { name: "Parts Finding", tag: "Tail", description: "Locate matched parts options and documentation quickly." },
      { name: "Agent Workflows", tag: "Tail", description: "Repeatable, reviewable AI-assisted tasks." },
      { name: "Evidence Packages", tag: "Tail", description: "Audit, pre-buy, and handoff packages." },
      { name: "Controlled Sharing", tags: ["Tail", "Org"], description: "Share evidence across organizations securely." },
    ],
    addOns: [
      {
        name: "Pre-buy Records Review",
        description: "Records gaps, conflicts, and a due-diligence package for aircraft purchases.",
      },
      {
        name: "Records Health Check",
        description: "Gap and conflict report on an aircraft's current records.",
      },
      {
        name: "Records Onboarding & Migration",
        description: "Bulk logbook extraction and ingestion into the Permanent Tail Record.",
      },
      {
        name: "Conformity Package",
        description: "Evidence assembled for new aircraft onboarding and conformity reviews.",
      },
      {
        name: "Sale-Ready Package",
        description: "Seller-side records package prepared ahead of buyer due diligence.",
      },
      {
        name: "Lease Return Package",
        description: "Records evidence supporting lease return conditions.",
      },
      {
        name: "Records Archive",
        description: "Read-only retention for sold, retired, or parted-out aircraft.",
      },
      {
        name: "Lender / Insurance Package",
        description: "Records summary for financing, appraisal, or underwriting.",
      },
      {
        name: "Export / Import Package",
        description: "Records prepared for registry transfer across borders.",
      },
      {
        name: "Manual Audit Pack",
        description: "Revision and audit-readiness review of policies and manuals.",
      },
    ],
    whoUses: [
      "Records / Closeout Specialist",
      "Director of Maintenance",
      "Quality / Compliance Manager",
      "Auditor (read-only)",
    ],
    contextTitle: "Vault - Intelligence in Vault - Records",
    contextName: "vaultIQ · oemIQ · troubleshootingIQ · partsIQ",
    contextSubtitle: "Reason across governed records, OEM content, troubleshooting context, and parts signals.",
    contextBullets: [
      "Find any record or supporting document in seconds",
      "Identify missing, conflicting, or stale records before they block work",
      "Assemble evidence packages with clearer maintenance and parts context",
    ],
    contextChips: ["Tail-grounded", "OEM content"],
    governanceNote: "Record acceptance and corrections stay with records personnel.",
    creditNote: "Capture is free. Document processing uses credits; first-year credits included per tail.",
    regulatoryAnchors: [
      "OpSpec/MSpec/LOA A025 — Applies when Vault - Records is the electronic recordkeeping system (optional)",
      "14 CFR §43.9 — Maintenance record entries",
      "14 CFR §43.11 — Inspection record entries",
      "14 CFR §91.417 — Maintenance records",
      "14 CFR §135.439 — Part 135 maintenance records",
    ],
    flow: ["Capture", "Classify", "Link to aircraft", "Review", "Share"],
  },
  operate: {
    eyebrow: "CONTINUOUS AIRWORTHINESS",
    moduleName: "Operate",
    valueLine: "Know the status of every aircraft, what is due, and the evidence behind it.",
    tagline: "Track. Forecast. Fly.",
    pricingRange: "{CONFIRM from Pricing Master} per aircraft / year",
    pricingUnitBadge: "Per tail",
    pricingNote: "Varies by aircraft classification band",
    features: [
      { name: "Aircraft Status", description: "Fleet and per-aircraft readiness at a glance." },
      { name: "Maintenance Program", description: "Program context, requirements, and revisions." },
      { name: "Due Items & Forecasting", description: "Upcoming inspections and limits, projected ahead." },
      { name: "AD / SB Evidence", description: "Applicability and compliance evidence, organized." },
      { name: "Life-Limited Parts", description: "Component and LLP status in context." },
      { name: "MEL & Deferrals", description: "Deferred items tracked to closure." },
      { name: "Conformity & Onboarding", description: "Evidence for new aircraft and conformity reviews." },
      { name: "Maintenance Control", description: "Coordinate work across bases, shops, and crews." },
      { name: "Human Intelligence", description: "Human-in-the-loop support for complex operational and maintenance decisions." },
    ],
    addOns: [
      {
        name: "Aircraft Induction & Operational Baseline",
        description: "Bring a new aircraft into service with a verified operational baseline.",
      },
      {
        name: "Maintenance-Program Conversion",
        description: "Transition aircraft to a new or revised maintenance program.",
      },
      {
        name: "Audit & Inspection Preparation",
        description: "Organize evidence ahead of regulatory audits and inspections.",
      },
      {
        name: "AD/SB Fleet Campaign Assessment",
        description: "Assess AD and SB applicability and status across the fleet.",
      },
      {
        name: "Fleet Records Remediation",
        description: "Resolve records gaps and conflicts across multiple aircraft.",
      },
      {
        name: "Conformity Remediation Planning",
        description: "Plan corrective actions to close conformity findings.",
      },
    ],
    whoUses: [
      "Director of Maintenance",
      "Maintenance Controller",
      "Records / Compliance Specialist",
      "Fleet / Aircraft Manager",
    ],
    contextTitle: "Vault - Intelligence in Operate",
    contextName: "complianceIQ",
    contextSubtitle: "Continuous-airworthiness intelligence for aircraft readiness, compliance, and operational decision support.",
    contextBullets: [
      "Understand what is due, deferred, restricted, or at risk",
      "Connect maintenance programs, ADs, SBs, MELs, and LLPs to operational context",
      "Link applicability, readiness, and compliance evidence to the aircraft",
      "Surface gaps before they affect aircraft availability",
      "Support conformity, onboarding, and audit-readiness workflows",
    ],
    contextChips: ["Tail-grounded", "Vault - Records evidence"],
    governanceNote: "Authorized personnel retain responsibility for applicability determinations, maintenance-program decisions, deferrals, airworthiness assessments, and operational release.",
    creditNote: "Compliance-critical processing uses credits.",
    regulatoryAnchors: [
      "14 CFR Part 39 — Airworthiness Directives",
      "14 CFR §91.213 — Inoperative instruments and equipment (MEL)",
      "14 CFR §91.409 — Inspections",
      "14 CFR §91.417 — Maintenance records",
      "14 CFR §135.411 / §135.419 — Maintenance programs",
      "14 CFR §135.439 — Maintenance recording requirements",
    ],
    flow: ["Plan", "Track", "Forecast", "Comply", "Release {CONFIRM}"],
  },
  shop: {
    eyebrow: "REPAIR STATION EXECUTION",
    moduleName: "Shop",
    valueLine: "Run work packages from intake to release with continuity across every shift.",
    tagline: "Execute. Inspect. Deliver.",
    pricingRange: "{CONFIRM from Pricing Master} per user / month",
    pricingUnitBadge: "Per user",
    features: [
      { name: "Work Packages", description: "Task visibility across every active event." },
      { name: "Job Cards", description: "Structured task execution and sign-off." },
      { name: "Parts & Tooling Readiness", description: "Know what is ready before work starts." },
      { name: "Shift", description: "Turnover that carries work forward between shifts." },
      { name: "Findings & Non-Routines", description: "Capture, route, and approve discrepancies." },
      { name: "Customer Authorization", description: "Customer approvals tied to the work event." },
      { name: "Inspection & Quality Routing", tag: "Standard+", description: "Required inspections routed to authorized personnel." },
      { name: "Authorization Records", description: "Who is authorized to inspect, sign, and release, linked to every job card." },
      { name: "Capability List", tag: "Standard+", description: "Manage your repair station capability list and its revisions." },
      { name: "eSignature", description: "Electronic signatures for approvals, sign-off, and return to service." },
      { name: "eSignature Controls", tag: "Standard+", description: "Multi-signer routing, signature authority rules, and audit-ready signature history." },
      { name: "Return to Service", description: "Release records with signer and authority context." },
      { name: "Delivery Package", description: "Structured, templated closeout for the customer." },
    ],
    addOns: [
      {
        name: "Customer Portal",
        tags: "Separately priced",
        description: "Customers track status, approve non-routines, and receive delivery packages online.",
      },
      {
        name: "Wearable Inspection & Evidence Capture",
        tags: "Separately priced",
        description: "Hands-free photo, video, and voice capture attached to the job card.",
      },
      {
        name: "Remote Inspection",
        tags: "Separately priced",
        description: "Live video inspection with an off-site inspector or customer representative.",
      },
    ],
    whoUses: [
      "Technician / Inspector",
      "Lead / Supervisor",
      "Quality Manager",
      "Production Control",
      "Customer Service Representative",
    ],
    contextTitle: "Vault - Intelligence in Shop",
    contextName: "shopIQ",
    contextSubtitle: "Keep work moving and documentation complete.",
    contextBullets: [
      "Surface bottlenecks and open items",
      "Track documentation completion",
      "Prepare delivery packages",
    ],
    contextChips: ["Tail-grounded", "Work event"],
    governanceNote: "Inspection, sign-off, and release stay with authorized personnel.",
    creditNote: "AI processing uses credits.",
    regulatoryAnchors: [
      "14 CFR §43.3 / §43.7 — Persons authorized to perform maintenance and approve return to service (covers Authorization Records and Return to Service; only anchor that applies to Independent non-145 users)",
      "14 CFR §43.9 — Maintenance record entries (covers Job Cards and Return to Service)",
      "14 CFR §43.13 — Performance rules (covers Job Cards and Parts & Tooling Readiness)",
      "14 CFR §145.155 / §145.157 — Inspection personnel and return-to-service authority (covers Inspection & Quality Routing and Authorization Records)",
      "14 CFR §145.161 — Records of management, supervisory and inspection personnel (covers Authorization Records)",
      "14 CFR §145.201 — Privileges and limitations (covers Capability List)",
      "14 CFR §145.211 — Quality control system (covers Inspection & Quality Routing)",
      "14 CFR §145.213 — Inspection of maintenance and approval for return to service (covers Inspection & Quality Routing and Return to Service)",
      "14 CFR §145.215 — Capability list (covers Capability List)",
      "14 CFR §145.219 — Recordkeeping (covers Delivery Package and record retention)",
    ],
    flow: ["Intake", "Inspect", "Work", "Sign-off", "Release"],
  },
  intelligence: {
    eyebrow: "AVIATION INTELLIGENCE",
    moduleName: "Vault - Intelligence",
    valueLine: "Ask, reason, and cite across aviation knowledge without loading an aircraft.",
    tagline: "Ask. Reason. Cite.",
    pricingRange: "No minimum · from 2.5¢ per credit",
    pricingUnitBadge: "Credits",
    pricingPillLabel: "View AI Credit Pricing",
    features: [
      { name: "Ask AI", description: "One conversational experience across aviOS." },
      { name: "Source-Cited Answers", description: "Every response linked to its source." },
      { name: "FAA Documents", description: "Reason across FAA publications, directives, advisory material, and guidance content." },
      { name: "Org Documents", description: "Use internal manuals, SOPs, policies, training materials, and organizational content in context." },
      { name: "1:1 & Group Chat", description: "Contextual conversations connected to teams, tasks, knowledge, and operations." },
      { name: "Voice & Video Calls", description: "Conduct one-on-one and group calls within the operational context." },
      { name: "Transcript Capture", description: "Convert voice and video conversations into searchable, reusable knowledge." },
      {
        name: "Capture Observations",
        description: "Capture field notes, emails, voice recordings, photos, wearable inputs, and direct observations.",
        details: [
          "Email",
          "Voice recordings (Plaud and similar devices)",
          "Wearable capture devices",
          "Mobile observations",
          "Technician notes",
          "Supervisor observations",
          "Human-generated evidence",
        ],
      },
      { name: "Human Intelligence", description: "Connect people, conversations, observations, and experts in the context of work." },
      { name: "Expert on Demand", description: "Qualified aviation experts available inside the workflow when needed." },
      { name: "Knowledge Capture", description: "Transform observations, conversations, transcripts, and validated outcomes into knowledge candidates." },
      { name: "Knowledge Curation", description: "Review, approve, organize, version, publish, and retire organizational knowledge." },
    ],
    addOns: [],
    whoUses: [
      "Technician",
      "Director of Maintenance",
      "Maintenance Controller",
      "Quality / Compliance Manager",
      "Knowledge Manager",
      "Pilot / Operations",
      "Any authorized employee",
    ],
    contextTitle: "How Credits Work",
    contextName: "AI Credits",
    contextSubtitle: "One currency for variable intelligence.",
    contextBullets: [
      "Capture is always free",
      "Processing, Q&A, and agent runs draw credits",
      "One pooled balance across modules",
    ],
    contextChips: ["Generic", "No tail grounding"],
    governanceNote: "Knowledge remains human-governed and subject to organizational approval.",
    creditNote: "Tail-grounded intelligence remains in Vault - Records, Operate, and Shop. Vault - Intelligence does not require a tail record.",
    contextLink: "Generic aviation intelligence, FAA knowledge, organizational knowledge, human collaboration, expert engagement, observation capture, and knowledge governance.",
    regulatoryAnchors: [
      "Human-in-the-loop on every regulated decision",
      "Source citation on every answer",
      "14 CFR §135.21 — Operator manual requirements (organization document)",
      "14 CFR §145.209 / §145.211 — Repair station and quality control manuals (organization documents)",
      "Uncertainty signaled, not hidden",
      "No approval, sign-off, or release authority",
    ],
    flow: ["Ask", "Retrieve", "Reason", "Cite", "Act"],
  },
};

export default function PublicApp() {
  const stackRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const forgeCardRef = useRef<HTMLElement | null>(null);
  const operateOutcomeRef = useRef<HTMLSpanElement | null>(null);
  const detailSectionRef = useRef<HTMLElement | null>(null);
  const cardRefs = useRef<Partial<Record<CapabilityCode, HTMLElement | null>>>({});
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const initialSlug = normalizePathToSlug(window.location.pathname);
    return initialSlug ? "detail" : "overview";
  });
  const viewModeRef = useRef<ViewMode>(viewMode);
  const transitionTimersRef = useRef<number[]>([]);
  const [pendingScrollSlug, setPendingScrollSlug] = useState<CapabilitySlug | null>(() => {
    return normalizePathToSlug(window.location.pathname);
  });
  const [arrowVisualMode, setArrowVisualMode] = useState<ViewMode>(viewMode);
  const [transitionMode, setTransitionMode] = useState<ViewMode | null>(null);
  const [outgoingMode, setOutgoingMode] = useState<ViewMode | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [connectorPositions, setConnectorPositions] = useState<ConnectorPositions>({
    base: 56,
    intelligence: 132,
    vault: 208,
    operateShop: 288,
  });
  const [forgeFooterTop, setForgeFooterTop] = useState<number | null>(null);
  const [pricingDrawerSlug, setPricingDrawerSlug] = useState<CapabilitySlug | null>(null);
  const [isPricingDrawerOpen, setIsPricingDrawerOpen] = useState(false);
  const [pricingDrawerView, setPricingDrawerView] = useState<"pricing" | "addons">("pricing");
  const pricingDrawerTimerRef = useRef<number | null>(null);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  useEffect(() => {
    if (!pricingDrawerSlug || !isPricingDrawerOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePricingDrawer();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pricingDrawerSlug, isPricingDrawerOpen]);

  useEffect(() => {
    return () => {
      transitionTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      transitionTimersRef.current = [];
      if (pricingDrawerTimerRef.current != null) {
        window.clearTimeout(pricingDrawerTimerRef.current);
        pricingDrawerTimerRef.current = null;
      }
    };
  }, []);

  function clearPricingDrawerTimer(): void {
    if (pricingDrawerTimerRef.current != null) {
      window.clearTimeout(pricingDrawerTimerRef.current);
      pricingDrawerTimerRef.current = null;
    }
  }

  function openPricingDrawer(slug: CapabilitySlug, view: "pricing" | "addons" = "pricing"): void {
    clearPricingDrawerTimer();
    setPricingDrawerView(view);
    setPricingDrawerSlug(slug);
    setIsPricingDrawerOpen(true);
  }

  function closePricingDrawer(): void {
    if (!pricingDrawerSlug && !isPricingDrawerOpen) {
      return;
    }
    clearPricingDrawerTimer();
    if (prefersReducedMotion()) {
      setIsPricingDrawerOpen(false);
      setPricingDrawerSlug(null);
      return;
    }
    setIsPricingDrawerOpen(false);
    pricingDrawerTimerRef.current = window.setTimeout(() => {
      pricingDrawerTimerRef.current = null;
      setPricingDrawerSlug(null);
    }, VIEW_TRANSITION_MS);
  }

  function resetPricingDrawer(): void {
    clearPricingDrawerTimer();
    setIsPricingDrawerOpen(false);
    setPricingDrawerSlug(null);
    setPricingDrawerView("pricing");
  }

  function clearTransitionTimers() {
    transitionTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    transitionTimersRef.current = [];
  }

  function queueTransitionTimer(callback: () => void, delayMs: number): void {
    const timerId = window.setTimeout(() => {
      transitionTimersRef.current = transitionTimersRef.current.filter((activeId) => activeId !== timerId);
      callback();
    }, delayMs);
    transitionTimersRef.current.push(timerId);
  }

  function transitionViewMode(nextMode: ViewMode, onComplete?: () => void): void {
    clearTransitionTimers();

    if (viewModeRef.current === nextMode) {
      setTransitionMode(null);
      setOutgoingMode(null);
      setIsTransitioning(false);
      setArrowVisualMode(nextMode);
      onComplete?.();
      return;
    }

    if (prefersReducedMotion()) {
      setTransitionMode(null);
      setOutgoingMode(null);
      setIsTransitioning(false);
      setViewMode(nextMode);
      setArrowVisualMode(nextMode);
      onComplete?.();
      return;
    }

    const previousMode = viewModeRef.current;
    setOutgoingMode(previousMode);
    setTransitionMode(nextMode);
    setIsTransitioning(true);
    setViewMode(nextMode);
    queueTransitionTimer(() => {
      setTransitionMode(null);
      setOutgoingMode(null);
      setIsTransitioning(false);
      setArrowVisualMode(nextMode);
      onComplete?.();
    }, VIEW_TRANSITION_MS);
  }

  const transitionClassName =
    transitionMode == null ? "" : transitionMode === "detail" ? "to-detail" : "to-overview";
  const arrowDockMode: ViewMode = transitionMode ?? viewMode;
  const arrowGlyph = arrowVisualMode === "overview" ? "↓" : "↑";
  const arrowAriaLabel =
    arrowVisualMode === "overview" ? "Show capability detail" : "Back to capabilities overview";
  const arrowTitle = arrowAriaLabel;

  function handleArrowAction(): void {
    if (isTransitioning) {
      return;
    }
    if (viewMode === "overview") {
      scrollToDetail("platform-base");
      return;
    }
    goToOverview();
  }

  useLayoutEffect(() => {
    const computeConnectorPositions = () => {
      if (viewMode !== "overview") {
        return;
      }
      const railEl = railRef.current;
      if (!railEl) {
        return;
      }

      const railRect = railEl.getBoundingClientRect();

      const centerY = (code: CapabilityCode): number | null => {
        const el = cardRefs.current[code];
        if (!el) {
          return null;
        }
        const rect = el.getBoundingClientRect();
        return rect.top + rect.height / 2 - railRect.top;
      };

      const base = centerY("PLATFORM_BASE");
      const intelligence = centerY("INTELLIGENCE");
      const vault = centerY("VAULT");
      const operate = centerY("OPERATE");
      const shop = centerY("SHOP");

      if (base == null || intelligence == null || vault == null || operate == null || shop == null) {
        return;
      }

      setConnectorPositions({
        base,
        intelligence,
        vault,
        operateShop: (operate + shop) / 2,
      });

      const operateOutcomeRect = operateOutcomeRef.current?.getBoundingClientRect();
      const forgeCardRect = forgeCardRef.current?.getBoundingClientRect();
      if (operateOutcomeRect && forgeCardRect) {
        const nextTop = Math.round(operateOutcomeRect.top - forgeCardRect.top);
        setForgeFooterTop((prev) => (prev === nextTop ? prev : nextTop));
      }
    };

    computeConnectorPositions();

    const observer = new ResizeObserver(() => {
      computeConnectorPositions();
    });

    if (stackRef.current) {
      observer.observe(stackRef.current);
    }
    if (railRef.current) {
      observer.observe(railRef.current);
    }
    capabilities.forEach((capability) => {
      const el = cardRefs.current[capability.code];
      if (el) {
        observer.observe(el);
      }
    });
    if (forgeCardRef.current) {
      observer.observe(forgeCardRef.current);
    }
    if (operateOutcomeRef.current) {
      observer.observe(operateOutcomeRef.current);
    }

    window.addEventListener("resize", computeConnectorPositions);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", computeConnectorPositions);
    };
  }, [viewMode]);

  useEffect(() => {
    const initialSlug = normalizePathToSlug(window.location.pathname);
    if (!initialSlug) {
      return;
    }
    setPendingScrollSlug(initialSlug);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const slug = normalizePathToSlug(window.location.pathname);
      if (!slug) {
        setPendingScrollSlug(null);
        transitionViewMode("overview", () => {
          window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? "auto" : "smooth" });
        });
        return;
      }
      setPendingScrollSlug(slug);
      transitionViewMode("detail");
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function scrollToDetail(slug: CapabilitySlug): void {
    setPendingScrollSlug(slug);
    const nextPath = capabilityPath(slug);
    if (window.location.pathname !== nextPath) {
      window.history.pushState({}, "", nextPath);
    }
    transitionViewMode("detail");
  }

  function goToOverview(): void {
    resetPricingDrawer();
    setPendingScrollSlug(null);
    if (window.location.pathname !== "/") {
      window.history.pushState({}, "", "/");
    }
    transitionViewMode("overview", () => {
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    });
  }

  function roleName(roleId: string): string {
    return modulesViewerContent.rolesById.get(roleId) ?? roleId;
  }

  function roleNamesForModule(slug: CapabilitySlug): string[] {
    const moduleRoles = modulesViewerContent.mappingsBySlug[slug].roles;
    const roleIds = Array.isArray(moduleRoles)
      ? moduleRoles
      : [
        ...(moduleRoles.primary ?? []),
        ...(moduleRoles.supporting ?? []),
        ...(moduleRoles.governance ?? []),
        ...(moduleRoles.external ?? []),
      ];
    const roleLabels = Array.from(new Set(roleIds)).map((roleId) => roleName(roleId));
    if ((slug === "intelligence" || slug === "forge") && !roleLabels.includes("Any Authorized Employee")) {
      roleLabels.push("Any Authorized Employee");
    }
    return roleLabels;
  }

  function orgTypeName(orgTypeId: string): string {
    return modulesViewerContent.orgTypesById.get(orgTypeId) ?? orgTypeId;
  }

  function moduleThemeClass(slug: CapabilitySlug): string {
    const accent = modulesViewerContent.modulesBySlug[slug]?.accent;
    return moduleAccentClass[accent] ?? "theme-platform";
  }

  function moduleDisplayName(slug: CapabilitySlug): string {
    if (slug === "intelligence") {
      return "Vault - Intelligence";
    }
    if (slug === "vault") {
      return "Vault - Records";
    }
    return modulesViewerContent.modulesBySlug[slug]?.title ?? capabilityLabel(slug).label;
  }

  function formatWorkflowSteps(slug: CapabilitySlug, steps: string[]): string {
    const treatment = workflowTreatmentByModule[slug];
    const separator = treatment.separator === "dot" ? " · " : " \u2192 ";
    const base = steps.join(separator);
    return treatment.loopBack && steps.length ? `${base} \u21ba ${steps[0]}` : base;
  }

  function renderHowItWorksBlock(slug: CapabilitySlug, steps: string[], options?: { hideTitle?: boolean }) {
    return (
      <section className="cap-section-card cap-workflow-strip">
        {options?.hideTitle ? null : <h2 className="cap-section-title">How it works</h2>}
        <div className="cap-workflow-centered">
          <p className="cap-workflow-steps-text">{formatWorkflowSteps(slug, steps)}</p>
        </div>
      </section>
    );
  }

  function formatFlowSteps(steps: string[]): string {
    return steps.join(" \u2192 ");
  }

  function renderPlatformBaseDetail() {
    const slug: CapabilitySlug = "platform-base";
    const moduleData = modulesViewerContent.modulesBySlug[slug];
    const mapping = modulesViewerContent.mappingsBySlug[slug];
    const pricingContent = modulesViewerContent.pricingEntriesById[moduleData.pricingId];
    const sharedBy = moduleData.sharedBy ?? [];
    const foundationAreas = [
      { name: "Tenant & Organization", summary: "Locations, aircraft, teams, and context." },
      { name: "Identity & Access", summary: "Users, SSO, MFA, and organizational boundaries." },
      { name: "Authority & Governance", summary: "Roles, credentials, signature authority, and oversight." },
      { name: "Data Governance", summary: "Tenant isolation, sharing controls, retention, and source-of-truth authority." },
      { name: "Security & Protection", summary: "Encryption in transit and at rest, monitoring, and backup and recovery." },
      { name: "Audit & Provenance", summary: "Tamper-evident history of every record, action, and decision." },
      { name: "AI Governance", summary: "Human authority, source citation, and controlled AI use." },
      { name: "Evidence Services", summary: "Documents, media, and supporting materials with provenance." },
      { name: "Workflow & Integration", summary: "Tasks, approvals, notifications, connectors, APIs, and event feeds." },
    ];
    const sharedRolesByModule = sharedBy.map((moduleSlug) => {
      const moduleLabel = moduleDisplayName(moduleSlug);
      return {
        moduleSlug,
        moduleLabel,
        roles: roleNamesForModule(moduleSlug),
      };
    });
    const administeringRoles = Array.isArray(mapping.roles)
      ? mapping.roles
      : [
        ...(mapping.roles.primary ?? []),
        ...(mapping.roles.supporting ?? []),
        ...(mapping.roles.governance ?? []),
        ...(mapping.roles.external ?? []),
      ];
    return (
      <div className="cap-detail-main">
        <section className="cap-section-card cap-standard-hero">
          <div className="cap-standard-hero-grid">
            <div>
              <p className="cap-detail-eyebrow">{moduleData.eyebrow}</p>
              <h1 className="cap-detail-title">{moduleData.title}</h1>
              <p className="cap-detail-description">{moduleData.valueStatement}</p>
              <p className="cap-detail-description cap-standard-tagline">{moduleData.outcome}</p>
              <div className="cap-chip-row">
                {sharedBy.map((moduleSlug) => (
                  <span key={moduleSlug} className={`cap-chip ${moduleThemeClass(moduleSlug)}`}>
                    {moduleDisplayName(moduleSlug)}
                  </span>
                ))}
              </div>
            </div>
            <aside className="cap-standard-pricing-card" aria-label={`${moduleData.title} pricing`}>
              <div className="cap-standard-pricing-header">
                <h2 className="cap-section-title">Pricing</h2>
              </div>
              <p className="cap-standard-pricing-range">{pricingContent.headline}</p>
              {pricingContent.subline ? <p className="cap-detail-description">{pricingContent.subline}</p> : null}
            </aside>
          </div>
        </section>

        <section className="cap-section-card">
          <h2 className="cap-section-title">Organization types</h2>
          <div className="cap-chip-row">
            {mapping.orgTypes.map((orgTypeId) => (
              <span key={orgTypeId} className="cap-chip">{orgTypeName(orgTypeId)}</span>
            ))}
          </div>
        </section>

        <section className="cap-section-card">
          <h2 className="cap-section-title">Foundation areas</h2>
          <div className="cap-feature-grid">
            {foundationAreas.map((area) => (
              <article key={area.name} className="cap-mini-tile">
                <h3>{area.name}</h3>
                <p>{area.summary}</p>
              </article>
            ))}
          </div>
        </section>

        <div className="cap-two-col-grid">
          <section className="cap-section-card">
            <h2 className="cap-section-title">Who administers it</h2>
            <ul className="cap-bullet-list">
              {administeringRoles.map((roleId) => (
                <li key={roleId}>{roleName(roleId)}</li>
              ))}
            </ul>
          </section>
          <section className="cap-section-card">
            <h2 className="cap-section-title">{moduleData.dataGovernance?.headline ?? "Data governance"}</h2>
            <ul className="cap-bullet-list">
              {(moduleData.dataGovernance?.points ?? []).map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>
        </div>

        <section className="cap-section-card">
          <h2 className="cap-section-title">Roles by Module</h2>
          <div className="cap-roles-module-grid">
            {sharedRolesByModule.map((moduleRoleSet) => (
              <article key={moduleRoleSet.moduleSlug} className="cap-roles-module-column">
                <h3>{moduleRoleSet.moduleLabel}</h3>
                <ul className="cap-bullet-list">
                  {moduleRoleSet.roles.map((role) => (
                    <li key={role}>{role}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>
      </div>
    );
  }

  function renderStandardModuleDetail(slug: StandardModuleSlug) {
    const moduleContent = standardWireframeContentByModule[slug];
    const whoUses = slug === "intelligence" && !moduleContent.whoUses.includes("Any Authorized Employee")
      ? [...moduleContent.whoUses, "Any Authorized Employee"]
      : moduleContent.whoUses;
    const usesAddOnDrawer = slug === "vault" || slug === "operate" || slug === "shop";
    const shouldStretchFeaturesCard = moduleContent.addOns.length === 0 || usesAddOnDrawer;
    const intelligenceFeatureGroups = [
      {
        title: "Knowledge Sources",
        features: moduleContent.features.filter((feature) => ["Ask AI", "Source-Cited Answers", "FAA Documents", "Org Documents"].includes(feature.name)),
      },
      {
        title: "Human Intelligence",
        features: moduleContent.features.filter((feature) => ["1:1 & Group Chat", "Voice & Video Calls", "Transcript Capture", "Capture Observations", "Human Intelligence", "Expert on Demand"].includes(feature.name)),
      },
      {
        title: "Knowledge Lifecycle",
        features: moduleContent.features.filter((feature) => ["Knowledge Capture", "Knowledge Curation"].includes(feature.name)),
      },
    ];

    return (
      <div className="cap-detail-main">
        <section className="cap-section-card cap-standard-hero">
          <div className="cap-standard-hero-grid">
            <div>
              <p className="cap-detail-eyebrow">{moduleContent.eyebrow}</p>
              <h1 className="cap-detail-title">{moduleContent.moduleName}</h1>
              <p className="cap-detail-description">{moduleContent.valueLine}</p>
              <p className="cap-detail-description cap-standard-tagline">{moduleContent.tagline}</p>
            </div>
            <aside className="cap-standard-pricing-card" aria-label={`${moduleContent.moduleName} pricing`}>
              <div className="cap-standard-pricing-header">
                <h2 className="cap-section-title">Pricing</h2>
                {usesAddOnDrawer ? (
                  <div className="cap-standard-pricing-pill-row">
                    <button
                      type="button"
                      className="cap-pricing-pill"
                      onClick={() => openPricingDrawer(slug, "pricing")}
                      aria-label={`Show pricing details for ${moduleContent.moduleName}`}
                    >
                      Pricing Factors
                    </button>
                    <button
                      type="button"
                      className="cap-pricing-pill"
                      onClick={() => openPricingDrawer(slug, "addons")}
                      aria-label={`Show add-ons for ${moduleContent.moduleName}`}
                    >
                      Add-ons
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="cap-pricing-pill"
                    onClick={() => openPricingDrawer(slug)}
                    aria-label={`Show what affects pricing for ${moduleContent.moduleName}`}
                  >
                    {moduleContent.pricingPillLabel ?? "What affects your pricing"}
                  </button>
                )}
              </div>
              {slug === "shop" ? (
                <>
                  <p className="cap-standard-pricing-range">From $299 / month</p>
                  <p className="cap-detail-description">Independent · Standard · Specialty · Enterprise. Scales with users and sites.</p>
                  <p className="cap-detail-description">AI credits included.</p>
                </>
              ) : slug === "operate" ? (
                <>
                  <p className="cap-standard-pricing-range">From $1,500 per aircraft / year, plus Vault - Records</p>
                  <p className="cap-detail-description">Scales with aircraft class, operating profile, fleet size, and regulatory scope.</p>
                  <p className="cap-detail-description">AI credits included.</p>
                </>
              ) : (
                <>
                  <p className="cap-standard-pricing-range">{moduleContent.pricingRange}</p>
                  {moduleContent.pricingNote ? <p className="cap-detail-description">{moduleContent.pricingNote}</p> : null}
                </>
              )}
            </aside>
          </div>
        </section>

        <div className="cap-standard-body-grid">
          <div className={`cap-standard-body-column${shouldStretchFeaturesCard ? " cap-standard-body-column-stretch" : ""}`}>
            <section className={`cap-section-card${shouldStretchFeaturesCard ? " cap-standard-features-card" : ""}`}>
              <div className="cap-section-title-row">
                <h2 className="cap-section-title">Features</h2>
                {usesAddOnDrawer ? (
                  <button
                    type="button"
                    className="cap-pricing-pill"
                    onClick={() => openPricingDrawer(slug, "addons")}
                    aria-label={`Show ${moduleContent.moduleName} add-ons`}
                  >
                    Add-ons
                  </button>
                ) : null}
              </div>
              {slug === "intelligence" ? (
                <div className="cap-feature-groups">
                  {intelligenceFeatureGroups.map((group) => (
                    <section key={group.title} className="cap-feature-group">
                      <h3 className="cap-feature-group-title">{group.title}</h3>
                      <div className="cap-standard-linear-list">
                        {group.features.map((feature) => (
                          <article key={feature.name} className="cap-mini-tile">
                            <div className="cap-standard-feature-row">
                              <h3>{feature.name}</h3>
                              {feature.tags?.length ? (
                                <div className="cap-standard-feature-tags">
                                  {feature.tags.map((tag) => (
                                    <span key={tag} className="cap-chip">{tag}</span>
                                  ))}
                                </div>
                              ) : feature.tag ? (
                                <span className="cap-chip">{feature.tag}</span>
                              ) : null}
                            </div>
                            <p>{feature.description}</p>
                            {feature.details?.length ? (
                              <>
                                <p className="cap-detail-description cap-list-heading">Supported sources include:</p>
                                <ul className="cap-bullet-list">
                                  {feature.details.map((detail) => (
                                    <li key={detail}>{detail}</li>
                                  ))}
                                </ul>
                              </>
                            ) : null}
                          </article>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="cap-standard-linear-list">
                  {moduleContent.features.map((feature) => (
                    <article key={feature.name} className="cap-mini-tile">
                      <div className="cap-standard-feature-row">
                        <h3>{feature.name}</h3>
                        {feature.tags?.length ? (
                          <div className="cap-standard-feature-tags">
                            {feature.tags.map((tag) => (
                              <span key={tag} className="cap-chip">{tag}</span>
                            ))}
                          </div>
                        ) : feature.tag ? (
                          <span className="cap-chip">{feature.tag}</span>
                        ) : null}
                      </div>
                      <p>{feature.description}</p>
                    </article>
                  ))}
                </div>
              )}
              {slug === "shop" ? (
                <p className="cap-detail-description cap-feature-footnote">* Available in Shop Standard and above.</p>
              ) : null}
            </section>

            {moduleContent.addOns.length && !usesAddOnDrawer ? (
              <section className="cap-section-card">
                <h2 className="cap-section-title">Add-ons</h2>
                <div className="cap-standard-linear-list">
                  {moduleContent.addOns.map((addOn) => (
                    <article key={addOn.name + addOn.tags} className="cap-mini-tile">
                      <div className="cap-standard-feature-row">
                        <h3>{addOn.name}</h3>
                        {addOn.tags ? <span className="cap-chip">{addOn.tags}</span> : null}
                      </div>
                      <p>{addOn.description}</p>
                    </article>
                  ))}
                </div>
              </section>
            ) : null}
          </div>

          <div className="cap-standard-body-column">
            <section className="cap-section-card">
              <h2 className="cap-section-title">Who uses it</h2>
              <ul className="cap-bullet-list">
                {whoUses.map((role) => (
                  <li key={role}>{role}</li>
                ))}
              </ul>
            </section>

            <section className="cap-section-card cap-intelligence-context">
              <h2 className="cap-section-title">{moduleContent.contextTitle}</h2>
              <p className="cap-detail-description cap-intelligence-name">{moduleContent.contextName}</p>
              <p className="cap-detail-description">{moduleContent.contextSubtitle}</p>
              <ul className="cap-bullet-list">
                {moduleContent.contextBullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="cap-chip-row">
                {moduleContent.contextChips.map((chip) => (
                  <span key={chip} className="cap-chip">{chip}</span>
                ))}
              </div>
              <p className="cap-detail-description">{moduleContent.governanceNote}</p>
              <p className="cap-detail-description">{moduleContent.creditNote}</p>
              {moduleContent.contextLink ? (
                <p className="cap-detail-description cap-standard-context-link">{moduleContent.contextLink}</p>
              ) : null}
            </section>

            <section className="cap-section-card">
              {slug === "intelligence" ? (
                <>
                  <h2 className="cap-section-title">Governance &amp; regulatory anchors</h2>
                  <p className="cap-anchor-subheading">General</p>
                  <ul className="cap-bullet-list">
                    <li>Source citation on every answer</li>
                    <li>Uncertainty signaled, not hidden</li>
                    <li>No tail grounding — generic, FAA, and organization content only</li>
                  </ul>
                  <p className="cap-anchor-subheading">FAA</p>
                  <ul className="cap-bullet-list">
                    <li>14 CFR §135.21 — Operator manual requirements</li>
                    <li>14 CFR §145.209 — Repair station manual</li>
                  </ul>
                  <p className="cap-anchor-subheading">Human Intelligence</p>
                  <ul className="cap-bullet-list">
                    <li>Human authority on every regulated decision</li>
                    <li>No approval, sign-off, or release authority</li>
                    <li>Experts advise; authorized personnel decide</li>
                  </ul>
                </>
              ) : (
                <>
                  <h2 className="cap-section-title">Regulatory anchors</h2>
                  <ul className="cap-bullet-list">
                    {moduleContent.regulatoryAnchors.map((anchor) => (
                      <li key={anchor}>{anchor}</li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          </div>
        </div>

        <section className="cap-section-card cap-workflow-strip">
          <div className="cap-workflow-centered">
            <p className="cap-workflow-steps-text">{formatFlowSteps(moduleContent.flow)}</p>
          </div>
        </section>
      </div>
    );
  }

  function renderForgeDetail() {
    const slug: CapabilitySlug = "forge";
    const forgeUsers = [
      "Flight line leaders and AI practitioners, including professional and non-professional programmers.",
    ];
    const intelligencePlatformItems = [
      { name: "aviOS MCP Server", description: "Expose aviOS capabilities through the Model Context Protocol for agent and AI integration." },
      { name: "Agent Runtime", description: "Execute and govern custom AI agents." },
      { name: "Tool Registry", description: "Register and manage tools available to human and AI workflows." },
      { name: "Prompt Templates", description: "Create reusable organizational prompts and workflows." },
      { name: "Workflow Orchestration", description: "Coordinate AI, human, and system activities across processes." },
      { name: "Credit Metering APIs", description: "Monitor and manage intelligence usage and consumption." },
    ];
    const experiencePlatformItems = [
      { name: "Custom Dashboards", description: "Build organization-specific operational views." },
      { name: "Custom Pages", description: "Create tailored experiences inside aviOS." },
      { name: "Custom Workspaces", description: "Design role-specific environments for teams and workflows." },
      { name: "Custom Workflow Steps", description: "Extend operational processes without modifying the platform." },
      { name: "Embedded Experiences", description: "Integrate Forge capabilities directly into business operations." },
    ];
    const integrationPlatformItems = [
      { name: "REST APIs", description: "Access aircraft, records, workflows, users, and operational data." },
      { name: "Vault APIs", description: "Interact with governed evidence, metadata, provenance, and evidence packages." },
      { name: "Webhooks", description: "Receive notifications when important aviation events occur." },
      { name: "Event Bus", description: "Publish and subscribe to platform events." },
      { name: "A2A Connectivity", description: "Secure application-to-application integration across aviOS and external systems." },
      { name: "Connectors", description: "Connect external platforms including maintenance systems, document repositories, collaboration tools, and aviation applications." },
    ];
    const developerPlatformItems = [
      { name: "aviOS SDK", description: "Build custom applications, automations, and extensions using supported development frameworks." },
      { name: "Authentication & Identity APIs", description: "Integrate users, organizations, permissions, and federated identity services." },
      { name: "Extension Framework", description: "Develop governed extensions that operate inside aviOS." },
      { name: "UI Components", description: "Build consistent user experiences using reusable platform components." },
      { name: "Testing & Validation Tools", description: "Validate integrations, extensions, workflows, and deployments before production release." },
    ];
    const governanceItems = [
      { name: "Extension Permissions", description: "Extensions must explicitly declare and request required permissions." },
      { name: "Tenant Isolation", description: "Data remains isolated and governed within customer boundaries." },
      { name: "Identity Federation", description: "Integrate with organizational identity providers." },
      { name: "Event Authorization", description: "Control access to events, workflows, and automations." },
      { name: "Audit & Provenance", description: "Track extension activity and platform interactions." },
      { name: "Human Governance", description: "Human authority remains required for regulated and business-critical decisions." },
    ];
    const forgeWorkflow = ["Build", "Connect", "Extend", "Automate", "Deliver"];

    return (
      <div className="cap-detail-main">
        <section className="cap-section-card cap-standard-hero">
          <div className="cap-standard-hero-grid">
            <div>
              <p className="cap-detail-eyebrow">FORGE: EXTENSIBILITY PLATFORM</p>
              <h1 className="cap-detail-title">Forge</h1>
              <p className="cap-detail-description">Build, integrate, automate, and extend aviOS using developer tools, APIs, agents, workflows, and ecosystem services.</p>
              <p className="cap-detail-description cap-standard-tagline cap-forge-tagline">
                <span className="cap-forge-tag-item">
                  <span className="cap-forge-tag-icon cap-forge-tag-icon-build" aria-hidden="true" />
                  Build
                </span>
                <span className="cap-forge-tag-separator">·</span>
                <span className="cap-forge-tag-item">
                  <span className="cap-forge-tag-icon cap-forge-tag-icon-connect" aria-hidden="true" />
                  Connect
                </span>
                <span className="cap-forge-tag-separator">·</span>
                <span className="cap-forge-tag-item">Extend</span>
              </p>
            </div>
            <aside className="cap-standard-pricing-card" aria-label="Forge pricing">
              <div className="cap-standard-pricing-header">
                <h2 className="cap-section-title">Pricing</h2>
                <button
                  type="button"
                  className="cap-pricing-pill"
                  onClick={() => openPricingDrawer(slug, "pricing")}
                  aria-label="Show what affects pricing for Forge"
                >
                  Pricing Options and SLAs
                </button>
              </div>
              <p className="cap-detail-description">Forge capabilities vary by integration scope, automation complexity, development requirements, and ecosystem participation.</p>
            </aside>
          </div>
        </section>

        <div className="cap-forge-top-stack">
          <section className="cap-section-card">
            <div className="cap-forge-build-three-col">
              <div>
                <h3 className="cap-section-title">What We Can Build Together</h3>
                <p className="cap-detail-description">Forge enables organizations to rapidly create solutions tailored to their unique operational requirements. Whether through customer-led innovation, AI-assisted development, collaborative design, or Ironfleet High Velocity Engineering (HVE), Forge dramatically accelerates the journey from idea to production.</p>
                <h3 className="cap-section-title cap-forge-col-secondary-title">Who uses it</h3>
                {forgeUsers.map((user) => (
                  <p key={user} className="cap-detail-description">{user}</p>
                ))}
              </div>
              <div className="cap-forge-build-middle-col">
                <h3 className="cap-section-title">Examples Include</h3>
                <ul className="cap-bullet-list">
                  <li>AI Agents and Digital Teammates</li>
                  <li>Operational Dashboards and Intelligence Views</li>
                  <li>Role-Specific Workspaces</li>
                  <li>Custom Workflow Automation</li>
                  <li>Maintenance and Compliance Solutions</li>
                  <li>Record Capture and Knowledge Experiences</li>
                  <li>Mobile and Field Applications</li>
                  <li>Integrations and Data Pipelines</li>
                  <li>Executive Reporting and Analytics</li>
                  <li>Industry-Specific Operational Tools</li>
                </ul>
              </div>
              <div>
                <h3 className="cap-section-title">Why It Matters</h3>
                <p className="cap-detail-description">Traditional aviation software often requires customers to adapt their operation to the software. Forge allows the software to adapt to the operation, enabling organizations to deliver new capabilities in days or weeks instead of waiting for product roadmaps or lengthy development cycles.</p>
              </div>
            </div>
          </section>
        </div>

        <section className="cap-section-card">
          <h2 className="cap-section-title">Governance &amp; Security</h2>
          <p className="cap-anchor-subheading">Platform Governance</p>
          <div className="cap-foundation-grid">
            {governanceItems.map((item) => (
              <article key={item.name} className="cap-foundation-tile">
                <h3>{item.name}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <div className="cap-two-col-grid">
          <section className="cap-section-card">
            <h2 className="cap-section-title">Intelligence Platform</h2>
            <p className="cap-detail-description">Launch trusted AI, orchestrated agents, and MCP-ready workflows that accelerate real operational decisions.</p>
            <div className="cap-standard-linear-list">
              {intelligencePlatformItems.map((item) => (
                <article key={item.name} className="cap-mini-tile">
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </section>
          <section className="cap-section-card">
            <h2 className="cap-section-title">Experience Platform</h2>
            <p className="cap-detail-description">Design role-perfect experiences with dashboards, pages, and workspaces that fit how your teams actually operate.</p>
            <div className="cap-standard-linear-list">
              {experiencePlatformItems.map((item) => (
                <article key={item.name} className="cap-mini-tile">
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </section>
        </div>
        <div className="cap-two-col-grid">
          <section className="cap-section-card">
            <h2 className="cap-section-title">Integration Platform</h2>
            <p className="cap-detail-description">Connect systems, data, APIs, and agents into one operational graph that moves information in real time.</p>
            <div className="cap-standard-linear-list">
              {integrationPlatformItems.map((item) => (
                <article key={item.name} className="cap-mini-tile">
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </section>
          <section className="cap-section-card">
            <h2 className="cap-section-title">Developer Platform</h2>
            <p className="cap-detail-description">Ship custom capability fast with SDKs, extension frameworks, and enterprise-grade developer tooling.</p>
            <div className="cap-standard-linear-list">
              {developerPlatformItems.map((item) => (
                <article key={item.name} className="cap-mini-tile">
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                </article>
              ))}
            </div>
          </section>
        </div>

        {renderHowItWorksBlock(slug, forgeWorkflow, { hideTitle: true })}
      </div>
    );
  }

  function renderPlaceholderDetail(slug: CapabilitySlug) {
    const { label, eyebrow } = capabilityLabel(slug);
    return (
      <div className="cap-detail-main">
        <section className="cap-section-card">
          <p className="cap-detail-eyebrow">{eyebrow}</p>
          <h1 className="cap-detail-title">{label}</h1>
          <p className="cap-detail-description">{placeholderDescription(label)}</p>
        </section>
        {renderPricingTile(slug)}
        <section className="cap-section-card">
          <h2 className="cap-section-title">Detail Experience</h2>
          <p className="cap-placeholder-text">Coming Soon</p>
        </section>
      </div>
    );
  }

  function renderPricingTile(slug: CapabilitySlug) {
    const moduleData = modulesViewerContent.modulesBySlug[slug];
    const pricingContent = modulesViewerContent.pricingEntriesById[moduleData.pricingId];
    const basis = pricingContent.basis.map((code) => pricingBasisLabelByCode[code] ?? code);
    return (
      <section className="cap-section-card">
        <div className="cap-pricing-header">
          <h2 className="cap-section-title">Pricing</h2>
          <button
            type="button"
            className="cap-pricing-pill"
            onClick={() => openPricingDrawer(slug)}
            aria-label={`Show what affects pricing for ${capabilityLabel(slug).label}`}
          >
            What affects your pricing
          </button>
        </div>
        <div className="cap-chip-row">
          {basis.map((item) => (
            <span key={item} className="cap-chip">{item}</span>
          ))}
        </div>
        <p className="cap-detail-description">{pricingContent.headline}</p>
        {pricingContent.subline ? <p className="cap-detail-description">{pricingContent.subline}</p> : null}
      </section>
    );
  }

  function renderCapabilitySection(slug: CapabilitySlug) {
    const { label } = capabilityLabel(slug);
    return (
      <section
        key={slug}
        className="cap-module-section"
        data-capability={slug}
        aria-label={`${label} page blocks`}
      >
        {slug === "platform-base"
          ? renderPlatformBaseDetail()
          : slug === "forge"
            ? renderForgeDetail()
            : slug === "intelligence" || slug === "vault" || slug === "operate" || slug === "shop"
              ? renderStandardModuleDetail(slug)
              : renderPlaceholderDetail(slug)}
      </section>
    );
  }

  function renderOverview() {
    return (
      <section className="capabilities-shell" aria-label="aviOS module overview">
        <div className="hero-title-row">
          <h1 className="hero-title hero-title-accent">aviOS Modules</h1>
          <div className="top-actions">
            <button type="button" className="btn btn-quiet">Talk to Ironfleet</button>
            <button type="button" className="btn btn-primary" disabled aria-disabled="true" title="Coming soon">
              Build an Estimate
            </button>
          </div>
        </div>
        <p className="hero-support">
          One connected maintenance experience, from aircraft requirement through completed record.
        </p>

        <div className="capability-layout">
          <div className="capability-stack" ref={stackRef}>
            {capabilities.map((capability) => (
              <article
                key={capability.code}
                className={`capability-card ${capability.themeClass}`}
                style={{ gridArea: stackArea(capability.code) }}
                ref={(el) => {
                  cardRefs.current[capability.code] = el;
                }}
                aria-label={`${capability.displayName} capability`}
                role="button"
                tabIndex={0}
                onClick={() => scrollToDetail(capability.slug)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    scrollToDetail(capability.slug);
                  }
                }}
              >
                <div className="capability-copy">
                  <div className="capability-eyebrow-row">
                    <span className="capability-eyebrow">{capability.eyebrow}</span>
                    <button
                      type="button"
                      className="capability-pill"
                      onClick={() => scrollToDetail(capability.slug)}
                      aria-label={`Explore ${capability.displayName}`}
                    >
                      Explore
                    </button>
                  </div>
                  <div className="capability-title-row">
                    <h2 className="capability-title">{capability.displayName}</h2>
                  </div>
                  <p className="capability-description">{capability.shortDescription}</p>
                  <span
                    className="capability-outcome"
                    ref={capability.code === "OPERATE" ? operateOutcomeRef : undefined}
                  >
                    {capability.outcomePhrase}
                  </span>
                </div>
              </article>
            ))}
          </div>

          <div className="extension-rail" aria-hidden="true" ref={railRef} style={asConnectorStyles(connectorPositions)}>
            <span className="rail-trunk" />
            <span className="rail-branch rail-branch-base" />
            <span className="rail-branch rail-branch-intelligence" />
            <span className="rail-branch rail-branch-vault" />
            <span className="rail-branch rail-branch-operate-shop" />
          </div>

          <aside
            className="extensions-card forge-card"
            aria-label="Forge"
            ref={forgeCardRef}
            role="button"
            tabIndex={0}
            onClick={() => scrollToDetail("forge")}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                scrollToDetail("forge");
              }
            }}
          >
            <div className="capability-eyebrow-row">
              <span className="capability-eyebrow">{extensions.eyebrow}</span>
              <button
                type="button"
                className="capability-pill"
                onClick={() => scrollToDetail("forge")}
                aria-label="Explore Forge"
              >
                Explore
              </button>
            </div>

            <h2 className="forge-card__title">{extensions.displayName}</h2>
            <p className="forge-card__description">{extensions.shortDescription}</p>

            <div className="forge-card__sections">
              <section className="forge-card__section" aria-label="Build modules">
                <h3 className="forge-card__section-title">BUILD</h3>
                <div className="forge-card__section-rule" aria-hidden="true" />
                <p className="forge-card__section-description">{extensions.buildDescription}</p>
                <ul className="forge-card__list">
                  {extensions.buildItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>

              <section className="forge-card__section" aria-label="Connect modules">
                <h3 className="forge-card__section-title">CONNECT</h3>
                <div className="forge-card__section-rule" aria-hidden="true" />
                <p className="forge-card__section-description">{extensions.connectDescription}</p>
                <ul className="forge-card__list">
                  {extensions.connectItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            </div>

            <p
              className="capability-outcome"
              style={forgeFooterTop == null ? undefined : { top: `${forgeFooterTop}px`, bottom: "auto" }}
            >
              {extensions.outcomePhrase}
            </p>
            <p className="mobile-extension-note">Extends Vault - Intelligence · Vault - Records · Operate · Shop</p>
          </aside>
        </div>

      </section>
    );
  }

  function renderDetail() {
    const activeSlug = pendingScrollSlug ?? normalizePathToSlug(window.location.pathname) ?? "platform-base";
    return (
      <section className="cap-detail-shell" aria-label="Capability detail section" ref={detailSectionRef}>
        <div className="cap-detail-layout">
          <div className="cap-detail-main-column">
            {renderCapabilitySection(activeSlug)}
          </div>
        </div>
        <footer className="cap-detail-footer-cta">
          <button type="button" className="btn btn-quiet">Talk to Ironfleet</button>
          <button type="button" className="btn btn-primary" disabled aria-disabled="true" title="Coming soon">
            Build an Estimate
          </button>
        </footer>
      </section>
    );
  }

  function renderDetailMode() {
    return (
      <>
        {renderDetail()}
        {renderPricingDrawer()}
      </>
    );
  }

  function renderPricingDrawer() {
    if (!pricingDrawerSlug) {
      return null;
    }

    const moduleData = modulesViewerContent.modulesBySlug[pricingDrawerSlug];
    const pricingContent = modulesViewerContent.pricingEntriesById[moduleData.pricingId];
    const { label } = capabilityLabel(pricingDrawerSlug);
    const extensionPricingRows = modulesViewerContent.mappingsBySlug[pricingDrawerSlug].extensions
      .map((extensionId) => modulesViewerContent.extensionsById.get(extensionId))
      .filter((extension): extension is NonNullable<typeof extension> => Boolean(extension))
      .map((extension) => ({
        label: extension.name,
        price: modulesViewerContent.extensionPricingById[extension.pricingId] ?? "Contact us",
      }));
    const drawerAddOns = pricingDrawerSlug === "intelligence" || pricingDrawerSlug === "vault" || pricingDrawerSlug === "operate" || pricingDrawerSlug === "shop"
      ? standardWireframeContentByModule[pricingDrawerSlug].addOns
      : [];
    const isShopPricingView = pricingDrawerSlug === "shop" && pricingDrawerView === "pricing";
    const isOperatePricingView = pricingDrawerSlug === "operate" && pricingDrawerView === "pricing";
    const operateAddOnTargetPriceByName: Record<string, string> = {
      "Aircraft Induction & Operational Baseline": "Target pricing: from $3,500 per aircraft",
      "Maintenance-Program Conversion": "Target pricing: from $2,500",
      "Audit & Inspection Preparation": "Target pricing: from $7,500 per certificate",
      "AD/SB Fleet Campaign Assessment": "Target pricing: from $3,500",
      "Fleet Records Remediation": "Target pricing: from $2,500",
      "Conformity Remediation Planning": "Target pricing: from $3,500",
    };

    return (
      <div className={`cap-pricing-drawer-shell ${isPricingDrawerOpen ? "is-open" : "is-closing"}`}>
        <div className="cap-pricing-drawer-backdrop" onClick={closePricingDrawer} />
        <aside
          className="cap-pricing-drawer-panel"
          role="dialog"
          aria-modal="true"
          aria-label={pricingDrawerView === "addons" ? `${label} add-ons` : `${label} pricing factors`}
        >
          <div className="cap-pricing-drawer-header">
            <div>
              {pricingDrawerView === "pricing" ? <p className="cap-detail-eyebrow">PRICING FACTORS</p> : null}
              <h3 className={pricingDrawerView === "addons" ? "cap-pricing-drawer-addons-title" : undefined}>
                {pricingDrawerView === "addons" ? `${label} Add-ons` : label}
              </h3>
            </div>
            <button type="button" className="cap-pricing-drawer-close" onClick={closePricingDrawer}>
              Close
            </button>
          </div>
          {pricingDrawerView === "pricing" ? (
            <>
              {isShopPricingView ? (
                <>
                  <div className="cap-chip-row">
                    {["Per Organization", "Per User", "Per Site"].map((basis) => (
                      <span key={basis} className="cap-chip">{basis}</span>
                    ))}
                  </div>
                  <p className="cap-detail-description"><strong>From $299 / month</strong></p>
                </>
              ) : isOperatePricingView ? (
                <>
                  <div className="cap-chip-row">
                    {["Per Tail", "Per Certificate"].map((basis) => (
                      <span key={basis} className="cap-chip">{basis}</span>
                    ))}
                  </div>
                  <p className="cap-detail-description"><strong>From $1,500 per aircraft / year, plus Vault - Records</strong></p>
                </>
              ) : (
                <>
                  <div className="cap-chip-row">
                    {pricingContent.basis.map((code) => (
                      <span key={code} className="cap-chip">{pricingBasisLabelByCode[code] ?? code}</span>
                    ))}
                  </div>
                  <p className="cap-detail-description">{pricingContent.headline}</p>
                  {pricingContent.subline ? <p className="cap-detail-description">{pricingContent.subline}</p> : null}
                </>
              )}
            </>
          ) : null}
          {pricingDrawerView === "addons" ? (
            <>
              <div className="cap-addon-list" aria-label={`${label} add-ons list`}>
                {drawerAddOns.map((addOn) => (
                  <article key={addOn.name} className="cap-addon-item">
                    <p className="cap-addon-name">{addOn.name}</p>
                    <p className="cap-addon-description">{addOn.description}</p>
                    {pricingDrawerSlug === "operate" ? (
                      <p className="cap-addon-target-price">{operateAddOnTargetPriceByName[addOn.name] ?? "Target pricing: quoted in estimate"}</p>
                    ) : null}
                  </article>
                ))}
              </div>
            </>
          ) : (
            <>
              {isShopPricingView ? (
                <>
                  <h4>How your price builds</h4>
                  <table className="cap-pricing-build-table" aria-label="Shop pricing components">
                    <thead>
                      <tr>
                        <th scope="col">Component</th>
                        <th scope="col">Pricing</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th scope="row">Platform Base</th>
                        <td>Included with every subscription</td>
                      </tr>
                      <tr>
                        <th scope="row">Shop tier</th>
                        <td>Independent from $299/mo · Standard from $599/mo · Specialty and Enterprise: Contact us</td>
                      </tr>
                      <tr>
                        <th scope="row">Additional users</th>
                        <td>Operational $89/mo · Professional $39/mo</td>
                      </tr>
                      <tr>
                        <th scope="row">Additional sites</th>
                        <td>$249/mo per administered site</td>
                      </tr>
                      <tr>
                        <th scope="row">Vault - Intelligence</th>
                        <td>Included credit allowance · then from 2.5¢ per credit</td>
                      </tr>
                      <tr>
                        <th scope="row">Vault - Records (optional)</th>
                        <td>From $899 per aircraft / year</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              ) : isOperatePricingView ? (
                <>
                  <h4>How your price builds</h4>
                  <table className="cap-pricing-build-table" aria-label="Operate pricing components">
                    <thead>
                      <tr>
                        <th scope="col">Component</th>
                        <th scope="col">Pricing</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th scope="row">Platform Base</th>
                        <td>Included with every subscription</td>
                      </tr>
                      <tr>
                        <th scope="row">Operate extension</th>
                        <td>From $1,500 per aircraft / year (B1–B5)</td>
                      </tr>
                      <tr>
                        <th scope="row">Vault - Records</th>
                        <td>Required for tail-grounded records, from $899 per aircraft / year</td>
                      </tr>
                      <tr>
                        <th scope="row">Regulatory scope</th>
                        <td>A025 readiness from $7,500 per certificate</td>
                      </tr>
                      <tr>
                        <th scope="row">Vault - Intelligence</th>
                        <td>Included credit allowance · then from 2.5¢ per credit</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              ) : null}
              <h4>What affects your pricing</h4>
              {isShopPricingView ? (
                <ul className="cap-pricing-factor-list">
                  <li><strong>Tier:</strong> Independent and Standard are self-serve. Specialty and Enterprise are quoted to match your certificates, ratings, and scale.</li>
                  <li><strong>Users:</strong> Each tier includes a base set of users. Additional Operational users cover dispatch, control, and active shop coordination. Additional Professional users cover records, administration, and limited planning. Customer participants are always included.</li>
                  <li><strong>Sites:</strong> Each additional administered site is priced separately.</li>
                  <li><strong>Vault - Intelligence:</strong> Every tier includes a credit allowance. Additional credits are billed only as you use them.</li>
                  <li><strong>Vault - Records:</strong> Optional. Add Vault - Records when your shop keeps governed, tail-grounded records for the aircraft it maintains, or uses Vault - Records as its electronic recordkeeping system.</li>
                  <li><strong>Onboarding:</strong> Migration and configuration are quoted in your estimate.</li>
                </ul>
              ) : isOperatePricingView ? (
                <ul className="cap-pricing-factor-list">
                  <li><strong>Aircraft class:</strong> Operate extension $1,500–$7,200 per tail/year (B1–B5).</li>
                  <li><strong>Operating profile:</strong> Part 135 and management companies may have an annual minimum.</li>
                  <li><strong>Fleet size:</strong> Volume pricing for larger fleets.</li>
                  <li><strong>Regulatory scope:</strong> A025 readiness priced per certificate, from $7,500.</li>
                  <li><strong>AI credits:</strong> Included pool; additional usage from 2.5¢ per credit.</li>
                  <li><strong>Onboarding:</strong> Migration and configuration quoted in your estimate.</li>
                </ul>
              ) : (
                <ul className="cap-pricing-factor-list">
                  {pricingContent.considerations.slice(0, 6).map((consideration) => (
                    <li key={consideration.factor}>
                      <strong>{consideration.factor}:</strong> {consideration.detail}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
          {pricingDrawerView === "pricing" && !isShopPricingView && !isOperatePricingView && extensionPricingRows.length ? (
            <>
              <h4>Extension pricing</h4>
              <ul className="cap-pricing-factor-list">
                {extensionPricingRows.map((entry) => (
                  <li key={entry.label}>
                    <strong>{entry.label}:</strong> {entry.price}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </aside>
      </div>
    );
  }

  const renderMode = (mode: ViewMode) => {
    if (mode === "overview") {
      return renderOverview();
    }
    return renderDetailMode();
  };

  return (
    <main className="overview-page capabilities-overview">
      <div className={`cap-mode-stack ${isTransitioning ? `is-transitioning ${transitionClassName}` : ""}`.trim()}>
        <div className="cap-mode-layer is-current" data-mode={viewMode}>
          {renderMode(viewMode)}
        </div>
        {isTransitioning && outgoingMode != null ? (
          <div className="cap-mode-layer is-outgoing" data-mode={outgoingMode}>
            {renderMode(outgoingMode)}
          </div>
        ) : null}
      </div>
      <div className={`cap-nav-center ${arrowDockMode === "detail" ? "dock-top" : "dock-bottom"}`}>
        <button
          type="button"
          className="cap-nav-fab"
          aria-label={arrowAriaLabel}
          title={arrowTitle}
          onClick={handleArrowAction}
          disabled={isTransitioning}
        >
          {arrowGlyph}
        </button>
      </div>
    </main>
  );
}
